import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { FileSelectEvent, FileUploadModule } from 'primeng/fileupload';
import { FieldsetModule } from 'primeng/fieldset';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { FirebaseStorageService } from '../../../services/firebase-storage.service';
import { BaseMasterDataService } from '../../../services/base-master-data.service';
import { SessionStorageService } from '../../../services/session-storage.service';
import { CompanyService } from '../../../company/company.service';
import { TenantScopeService } from '../../../company/tenant-scope.service';
import { Country } from '../../../models/common/country.model';
import { City } from '../../../models/common/city.model';
import { PageFormSectionComponent } from '../../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { PayloadType } from '../../../models/meta-params/payload-type';
import { TripType } from '../../../models/vehicle/trip-type';
import { Vehicle } from '../vehicle.model';
import { VehicleService } from '../vehicle.service';
import { VehicleCapacity } from '../params/vehicle-capacity';
import { VehicleConfig } from '../params/vehicle-config';
import { VehicleMake } from '../params/vehicle-make';
import { VehicleModel } from '../params/vehicle-model';
import { VehicleType } from '../params/vehicle-type';
import { VehicleYear } from '../params/vehicle-year';
import { CrmClient } from '../../../crm/crm.model';
import { CrmService } from '../../../crm/crm.service';
import { resolveSisterCompanyScope, sisterCompanyFleetAccessMessage } from '../../../shared/utils/sister-company-scope.util';

@Component({
    selector: 'app-edit-vehicle',
    templateUrl: './edit-vehicle.component.html',
    styleUrls: ['./edit-vehicle.component.css'],
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        RouterModule,
        ToastModule,
        InputTextModule,
        ButtonModule,
        SelectModule,
        BreadcrumbModule,
        FileUploadModule,
        DatePickerModule,
        TextareaModule,
        MultiSelectModule,
        ProgressBarModule,
        FieldsetModule,
        InputGroupModule,
        InputGroupAddonModule,
        IftaLabelModule,
        CheckboxModule,
        PageShellComponent,
        PageHeroComponent,
        PageFormSectionComponent
    ],
    providers: [
        MessageService,
        FirebaseStorageService,
        SessionStorageService,
        VehicleService,
        BaseMasterDataService
    ]
})
export class EditVehicleComponent implements OnInit {

    @Input() embedded = false;
    @Input() embeddedSource: 'owned' | 'freelance' | 'rental' | 'transporter' = 'owned';
    @Output() saved = new EventEmitter<Vehicle>();
    @Output() cancelled = new EventEmitter<void>();

    createEditVehicleForm!: FormGroup<any>;

    vehicleId!: number;
    isLoading = false;
    loadError: string | null = null;
    vehicle: Vehicle = new Vehicle();
    vehicleCreationInProgress = false;
    pdfUploadProgress = 0;

    selectedVehicleType?: VehicleType;
    selectedVehicleConfig?: VehicleConfig;
    selectedVehicleCapacity?: VehicleCapacity;
    selectedVehicleMake?: VehicleMake;
    selectedVehicleModel?: VehicleModel;
    selectedVehicleYear?: VehicleYear;
    selectedPayloadTypes: PayloadType[] = [];
    selectedTripTypes: TripType[] = [];
    selectedRegistrationDate?: Date;
    selectedRegistrationIssueDate?: Date;
    selectedInsuranceIssueDate?: Date;
    selectedInsuranceExpiryDate?: Date;
    selectedTechnicalInspectionIssueDate?: Date;
    selectedTechnicalInspectionExpiryDate?: Date;
    selectedOperatingCardIssueDate?: Date;
    selectedOperatingCardExpiryDate?: Date;
    selectedRegistrationCountry?: Country;
    selectedRegistrationCity?: City;
    selectedOperatingCardCountry?: Country;
    selectedInsuranceCompany: any = null;
    selectedTransporterClient?: CrmClient;

    vehicleTypes: VehicleType[] = [];
    vehicleConfigs: VehicleConfig[] = [];
    vehicleCapacities: VehicleCapacity[] = [];
    vehicleMakes: VehicleMake[] = [];
    vehicleModels: VehicleModel[] = [];
    vehicleYears: VehicleYear[] = [];
    payloadTypes: PayloadType[] = [];
    tripTypes: TripType[] = [];
    countries: Country[] = [];
    registrationCities: City[] = [];
    insuranceCompanies: any[] = [];
    transporterClients: CrmClient[] = [];

    readonly registrationUsageOptions = [
        { label: 'Commercial', value: 'COMMERCIAL' },
        { label: 'Private', value: 'PRIVATE' },
        { label: 'Government', value: 'GOVERNMENT' }
    ];

    readonly ownershipTypeOptions = [
        { label: 'Company Owned', value: 'COMPANY_OWNED' },
        { label: 'Leased', value: 'LEASED' },
        { label: 'Rented', value: 'RENTED' },
        { label: 'Third Party', value: 'THIRD_PARTY' }
    ];

    readonly plateSourceOptions = [
        { label: 'National', value: 'NATIONAL' },
        { label: 'Export', value: 'EXPORT' },
        { label: 'Temporary', value: 'TEMPORARY' },
        { label: 'Custom', value: 'CUSTOM' }
    ];

    readonly plateTypeOptions = [
        { label: 'Commercial', value: 'COMMERCIAL' },
        { label: 'Private', value: 'PRIVATE' },
        { label: 'Government', value: 'GOVERNMENT' },
        { label: 'Trailer', value: 'TRAILER' }
    ];

    readonly plateColorOptions = [
        { label: 'White', value: 'WHITE' },
        { label: 'Yellow', value: 'YELLOW' },
        { label: 'Blue', value: 'BLUE' },
        { label: 'Red', value: 'RED' },
        { label: 'Green', value: 'GREEN' }
    ];

    readonly operatingCardStatusOptions = [
        { label: 'Active', value: 'ACTIVE' },
        { label: 'Suspended', value: 'SUSPENDED' },
        { label: 'Expired', value: 'EXPIRED' }
    ];

    formTitle = '';
    isFreelancer = false;
    isRental = false;
    private currentCompanyId: number | null = null;
    private companyGroupId: number | null = null;

    breadcrumbItems: MenuItem[] = [];
    homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/dashboard' };

    constructor(
        private sessionStorageService: SessionStorageService,
        private route: ActivatedRoute,
        private vehicleService: VehicleService,
        private messageService: MessageService,
        private router: Router,
        private firebaseStorageService: FirebaseStorageService,
        private companyService: CompanyService,
        private baseMasterDataService: BaseMasterDataService,
        private tenantScopeService: TenantScopeService,
        private crmService: CrmService
    ) { }

    ngOnInit(): void {
        this.currentCompanyId = this.sessionStorageService.getCompany()?.id ?? null;
        const vehicleId = this.route.snapshot.paramMap.get('id');
        const isCreate = !vehicleId;

        this.createEditVehicleForm = new FormGroup({
            plateNumberInput: new FormControl('', [Validators.required, Validators.pattern(/^[A-Za-z0-9\s]+$/)]),
            engineNumberInput: new FormControl(''),
            registrationNumberInput: new FormControl(''),
            vehicleTypeDropdown: new FormControl('', Validators.required),
            vehicleConfigDropdown: new FormControl('', Validators.required),
            vehicleCapacityDropdown: new FormControl('', Validators.required),
            vehicleMakeDropdown: new FormControl(''),
            vehicleModelDropdown: new FormControl(''),
            vehicleYearDropdown: new FormControl(''),
            payloadTypesMultiSelect: new FormControl(''),
            tripTypesMultiSelect: new FormControl(''),
            registrationDateInput: new FormControl(''),
            registrationCountryDropdown: new FormControl('', isCreate ? Validators.required : []),
            registrationCityDropdown: new FormControl('', isCreate ? Validators.required : []),
            chassisNumberInput: new FormControl(''),
            insuranceCompanyDropdown: new FormControl(''),
            registrationIssueDateInput: new FormControl(''),
            insuranceIssueDateInput: new FormControl(''),
            insuranceExpiryDateInput: new FormControl(''),
            technicalInspectionIssueDateInput: new FormControl(''),
            technicalInspectionExpiryDateInput: new FormControl(''),
            operatingCardIssueDateInput: new FormControl(''),
            operatingCardExpiryDateInput: new FormControl('')
        });
        this.createEditVehicleForm.get('plateNumberInput')?.valueChanges.subscribe(value => {
            const normalized = typeof value === 'string'
                ? value.replace(/\s+/g, '').toUpperCase()
                : value;
            if (normalized !== value) {
                this.createEditVehicleForm.get('plateNumberInput')?.setValue(normalized, { emitEvent: false });
                this.vehicle.plateNumber = normalized;
            }
        });

        this.refreshCompanyFleetProfile();
        this.initializeResourceContext();
        this.loadTransporterClients();
        this.loadCountries();
        this.loadVehicleTypes();
        this.loadVehicleConfigs();
        this.loadVehicleCapacities();
        this.loadVehicleMakes();
        this.loadVehicleModels();
        this.loadVehicleYears();
        this.loadPayloadTypes();
        this.loadTripTypes();

        if (vehicleId) {
            this.formTitle = 'Update Vehicle';
            this.vehicleId = Number(vehicleId);
            this.initializeScopeAndLoadVehicle();
        } else {
            this.formTitle = 'Create Vehicle';
            this.initializeForNewVehicle();
        }

        this.breadcrumbItems = [
            { label: 'Fleet', routerLink: '/fleet' },
            { label: 'Vehicles', routerLink: '/fleet/vehicles' },
            { label: vehicleId ? 'Update' : 'Create' }
        ];
    }

    initializeForNewVehicle(): void {
        this.vehicle = new Vehicle();
        this.vehicle.isInsured = false;
        const registrationNumber = this.route.snapshot.queryParamMap.get('registrationNumber');
        if (registrationNumber) {
            this.vehicle.registrationNumber = registrationNumber;
            this.createEditVehicleForm.get('registrationNumberInput')?.setValue(registrationNumber);
        }
    }

    loadVehicle(): void {
        this.isLoading = true;
        this.loadError = null;
        this.vehicleService.findVehicleByIds(
            'en-US',
            [this.vehicleId],
            this.currentCompanyId ?? undefined,
            this.companyGroupId ?? undefined
        ).subscribe({
            next: vehicles => {
                this.vehicle = vehicles.data[0];
                if (this.vehicle?.resourceCompanyId && this.currentCompanyId && this.vehicle.resourceCompanyId !== this.currentCompanyId) {
                    this.blockSisterCompanyEdit();
                    return;
                }
                this.selectedRegistrationDate = this.toDate(this.vehicle.registrationExpiry);
                this.selectedRegistrationIssueDate = this.toDate(this.vehicle.registrationIssueDate);
                this.selectedInsuranceIssueDate = this.toDate(this.vehicle.insuranceIssueDate);
                this.selectedInsuranceExpiryDate = this.toDate(this.vehicle.insuranceExpiry);
                this.selectedTechnicalInspectionIssueDate = this.toDate(this.vehicle.technicalInspectionIssueDate);
                this.selectedTechnicalInspectionExpiryDate = this.toDate(this.vehicle.technicalInspectionExpiryDate);
                this.selectedOperatingCardIssueDate = this.toDate(this.vehicle.operatingCardIssueDate);
                this.selectedOperatingCardExpiryDate = this.toDate(this.vehicle.operatingCardExpiryDate);
                this.updateFormValues();
                this.isLoading = false;
            },
            error: error => {
                this.loadError = error?.status === 403 && this.route.snapshot.queryParamMap.get('resourceView') === 'sister'
                    ? sisterCompanyFleetAccessMessage('vehicle')
                    : 'Unable to load this vehicle for editing.';
                this.isLoading = false;
            }
        });
    }

    private initializeScopeAndLoadVehicle(): void {
        const applyScope = () => {
            this.companyGroupId = resolveSisterCompanyScope(
                this.tenantScopeService.getAccessSummary(),
                this.currentCompanyId,
                ['fleet.vehicles.view']
            )?.companyGroupId ?? null;
            this.loadVehicle();
        };

        this.tenantScopeService.initializeCurrentAccess().subscribe(() => applyScope());
    }

    private blockSisterCompanyEdit(): void {
        this.isLoading = false;
        this.messageService.add({
            severity: 'warn',
            summary: 'Read-only record',
            detail: 'Sister-company vehicles can be viewed here, but not edited from this company.'
        });
        setTimeout(() => {
            this.router.navigate(['/fleet/vehicle/view', this.vehicleId], {
                queryParams: this.route.snapshot.queryParams
            });
        }, 150);
    }

    updateFormValues(): void {
        this.selectedVehicleType = this.vehicleTypes.find(value => value.id === this.vehicle.type?.id) || this.vehicle.type;
        this.selectedVehicleConfig = this.vehicleConfigs.find(value => value.configId === this.vehicle.config?.configId) || this.vehicle.config;
        this.selectedVehicleCapacity = this.vehicleCapacities.find(value => value.capacityId === this.vehicle.capacity?.capacityId) || this.vehicle.capacity;
        this.selectedVehicleMake = this.vehicleMakes.find(value => value.makeId === this.vehicle.make?.makeId) || this.vehicle.make;
        this.selectedVehicleModel = this.vehicleModels.find(value => value.modelId === this.vehicle.model?.modelId) || this.vehicle.model;
        this.selectedVehicleYear = this.vehicleYears.find(value => value.yearId === this.vehicle.year?.yearId) || this.vehicle.year;
        this.selectedPayloadTypes = this.vehicle.payloadTypes || [];
        this.selectedTripTypes = this.vehicle.tripTypes || [];
        this.selectedRegistrationCountry = this.countries.find(country => country.id === this.vehicle.registrationCountry?.id) || this.vehicle.registrationCountry;
        this.selectedOperatingCardCountry = this.countries.find(country => country.id === this.vehicle.operatingCardCountry?.id) || this.vehicle.operatingCardCountry;
        this.syncSelectedTransporterClient();

        if (this.selectedRegistrationCountry?.id) {
            this.loadRegistrationCities(this.selectedRegistrationCountry.id, this.vehicle.registrationCity?.id ?? null);
            this.loadInsuranceCompanies(this.selectedRegistrationCountry.id, this.vehicle.insuranceCompany?.companyId ?? null);
        }

        this.createEditVehicleForm.patchValue({
            plateNumberInput: this.vehicle.plateNumber,
            engineNumberInput: this.vehicle.engineNumber,
            registrationNumberInput: this.vehicle.registrationNumber,
            vehicleTypeDropdown: this.selectedVehicleType,
            vehicleConfigDropdown: this.selectedVehicleConfig,
            vehicleCapacityDropdown: this.selectedVehicleCapacity,
            vehicleMakeDropdown: this.selectedVehicleMake,
            vehicleModelDropdown: this.selectedVehicleModel,
            vehicleYearDropdown: this.selectedVehicleYear,
            payloadTypesMultiSelect: this.selectedPayloadTypes,
            tripTypesMultiSelect: this.selectedTripTypes,
            registrationDateInput: this.selectedRegistrationDate,
            registrationCountryDropdown: this.selectedRegistrationCountry,
            registrationCityDropdown: this.selectedRegistrationCity,
            chassisNumberInput: this.vehicle.chassisNumber,
            insuranceCompanyDropdown: this.selectedInsuranceCompany,
            registrationIssueDateInput: this.selectedRegistrationIssueDate,
            insuranceIssueDateInput: this.selectedInsuranceIssueDate,
            insuranceExpiryDateInput: this.selectedInsuranceExpiryDate,
            technicalInspectionIssueDateInput: this.selectedTechnicalInspectionIssueDate,
            technicalInspectionExpiryDateInput: this.selectedTechnicalInspectionExpiryDate,
            operatingCardIssueDateInput: this.selectedOperatingCardIssueDate,
            operatingCardExpiryDateInput: this.selectedOperatingCardExpiryDate
        });
    }

    loadCountries(): void {
        this.countries = this.sessionStorageService.getCountryList() || [];
        if (this.countries.length) {
            return;
        }
        this.baseMasterDataService.getCountries('en-US').subscribe(countries => {
            this.countries = countries || [];
            if (this.vehicle.registrationCountry?.id) {
                this.selectedRegistrationCountry = this.countries.find(country => country.id === this.vehicle.registrationCountry?.id) || this.vehicle.registrationCountry;
            }
            if (this.vehicle.operatingCardCountry?.id) {
                this.selectedOperatingCardCountry = this.countries.find(country => country.id === this.vehicle.operatingCardCountry?.id) || this.vehicle.operatingCardCountry;
            }
        });
    }

    loadRegistrationCities(countryId: number, preferredCityId?: number | null): void {
        this.baseMasterDataService.getCities('en-US', countryId).subscribe(cities => {
            this.registrationCities = cities || [];
            if (preferredCityId) {
                this.selectedRegistrationCity = this.registrationCities.find(city => city.id === preferredCityId)
                    || this.vehicle.registrationCity;
                this.createEditVehicleForm.get('registrationCityDropdown')?.setValue(this.selectedRegistrationCity || null);
            }
        });
    }

    loadInsuranceCompanies(countryId: number, preferredCompanyId?: number | null): void {
        this.vehicleService.findAllGoodsInsuranceCompanies(countryId, 'en-US').subscribe((companies: any) => {
            this.insuranceCompanies = Array.isArray(companies) ? companies : [];
            if (preferredCompanyId) {
                this.selectedInsuranceCompany = this.insuranceCompanies.find(company => company.companyId === preferredCompanyId)
                    || this.vehicle.insuranceCompany
                    || null;
                this.createEditVehicleForm.get('insuranceCompanyDropdown')?.setValue(this.selectedInsuranceCompany || null);
            }
        });
    }

    loadTransporterClients(): void {
        if (!this.currentCompanyId || (!this.isFreelancer && !this.isRental)) {
            this.transporterClients = [];
            this.selectedTransporterClient = undefined;
            return;
        }
        this.crmService.getClients(this.currentCompanyId, ['TRANSPORTER']).subscribe(clients => {
            this.transporterClients = clients ?? [];
            this.syncSelectedTransporterClient();
        });
    }

    loadVehicleTypes(): void {
        this.vehicleTypes = this.sessionStorageService.getVehicleTypes() || [];
    }

    loadVehicleConfigs(): void {
        this.vehicleConfigs = this.selectedVehicleType
            ? this.sessionStorageService.getVehicleTypes()?.filter(vehicleType => vehicleType.id === this.selectedVehicleType?.id)
                .flatMap(vehicleType => vehicleType.vehicleConfigs) || []
            : this.sessionStorageService.getVehicleTypes()?.flatMap(vehicleType => vehicleType.vehicleConfigs) || [];
    }

    loadVehicleCapacities(): void {
        this.vehicleCapacities = this.selectedVehicleType
            ? this.sessionStorageService.getVehicleTypes()?.filter(vehicleType => vehicleType.id === this.selectedVehicleType?.id)
                .flatMap(vehicleType => vehicleType.vehicleCapacities) || []
            : this.sessionStorageService.getVehicleTypes()?.flatMap(vehicleType => vehicleType.vehicleCapacities) || [];
    }

    loadVehicleMakes(): void {
        this.vehicleMakes = this.sessionStorageService.getVehicleMakes() || [];
    }

    loadVehicleModels(): void {
        this.vehicleModels = this.selectedVehicleMake
            ? this.sessionStorageService.getVehicleMakes()?.filter(vehicleMake => vehicleMake.makeId === this.selectedVehicleMake?.makeId)
                .flatMap(vehicleMake => vehicleMake.models) || []
            : this.sessionStorageService.getVehicleMakes()?.flatMap(vehicleMake => vehicleMake.models) || [];
    }

    loadVehicleYears(): void {
        this.vehicleYears = this.selectedVehicleModel
            ? this.vehicleModels.filter(vehicleModel => vehicleModel.modelId === this.selectedVehicleModel?.modelId)
                .flatMap(vehicleModel => vehicleModel.years) || []
            : this.sessionStorageService.getVehicleMakes()?.flatMap(vehicleMake => vehicleMake.models)
                .flatMap(vehicleModel => vehicleModel.years) || [];
    }

    loadPayloadTypes(): void {
        this.payloadTypes = this.sessionStorageService.getPayloadTypes() || [];
    }

    loadTripTypes(): void {
        this.tripTypes = this.sessionStorageService.getTripTypes() || [];
    }

    onVehicleTypeChange(event: any): void {
        this.selectedVehicleType = event.value;
        if (this.selectedVehicleType) {
            this.vehicle.type = this.selectedVehicleType;
        }
        this.loadVehicleConfigs();
        this.loadVehicleCapacities();
    }

    onVehicleConfigChange(event: any): void {
        this.selectedVehicleConfig = event.value;
        if (this.selectedVehicleConfig) {
            this.vehicle.config = this.selectedVehicleConfig;
        }
    }

    onVehicleCapacityChange(event: any): void {
        this.selectedVehicleCapacity = event.value;
        if (this.selectedVehicleCapacity) {
            this.vehicle.capacity = this.selectedVehicleCapacity;
        }
    }

    onVehicleMakeChange(event: any): void {
        this.selectedVehicleMake = event.value;
        if (this.selectedVehicleMake) {
            this.vehicle.make = this.selectedVehicleMake;
        }
        this.loadVehicleModels();
        this.loadVehicleYears();
    }

    onVehicleModelChange(event: any): void {
        this.selectedVehicleModel = event.value;
        if (this.selectedVehicleModel) {
            this.vehicle.model = this.selectedVehicleModel;
        }
        this.loadVehicleYears();
    }

    onVehicleYearChange(event: any): void {
        this.selectedVehicleYear = event.value;
        if (this.selectedVehicleYear) {
            this.vehicle.year = this.selectedVehicleYear;
        }
    }

    onRegistrationCountryChange(event: any): void {
        this.selectedRegistrationCountry = event.value;
        this.selectedRegistrationCity = undefined;
        this.registrationCities = [];
        this.selectedInsuranceCompany = null;
        this.insuranceCompanies = [];
        this.createEditVehicleForm.get('registrationCityDropdown')?.setValue(null);
        this.createEditVehicleForm.get('insuranceCompanyDropdown')?.setValue(null);
        if (this.selectedRegistrationCountry?.id) {
            this.loadRegistrationCities(this.selectedRegistrationCountry.id);
            this.loadInsuranceCompanies(this.selectedRegistrationCountry.id);
        }
    }

    onRegistrationCityChange(event: any): void {
        this.selectedRegistrationCity = event.value;
    }

    onOperatingCardCountryChange(event: any): void {
        this.selectedOperatingCardCountry = event.value;
    }

    onInsuranceCompanyChange(event: any): void {
        this.selectedInsuranceCompany = event.value;
    }

    onTransporterClientChange(event: any): void {
        this.selectedTransporterClient = event.value || undefined;
        this.vehicle.transporterCrmClientId = this.selectedTransporterClient?.id;
    }

    onRegistrationCardUpload(event: FileSelectEvent): void {
        this.uploadFile(event.files[0], 'REGISTRATION');
    }

    onInsuranceDocumentUpload(event: FileSelectEvent): void {
        this.uploadFile(event.files[0], 'INSURANCE');
    }

    onTechnicalInspectionDocumentUpload(event: FileSelectEvent): void {
        this.uploadFile(event.files[0], 'TECHNICAL_INSPECTION');
    }

    onOperatingCardDocumentUpload(event: FileSelectEvent): void {
        this.uploadFile(event.files[0], 'OPERATING_CARD');
    }

    uploadFile(file: File, fileType: 'REGISTRATION' | 'INSURANCE' | 'TECHNICAL_INSPECTION' | 'OPERATING_CARD'): void {
        this.vehicleCreationInProgress = true;
        this.pdfUploadProgress = 0;
        const storagePath = `vehicles/${fileType.toLowerCase()}`;

        this.firebaseStorageService.uploadFileWithProgress(file, storagePath).subscribe({
            next: upload => {
                this.pdfUploadProgress = upload.progress;
                if (upload.url) {
                    if (fileType === 'REGISTRATION') {
                        this.vehicle.registrationImage = upload.url;
                    } else if (fileType === 'INSURANCE') {
                        this.vehicle.insuranceDocumentUrl = upload.url;
                    } else if (fileType === 'TECHNICAL_INSPECTION') {
                        this.vehicle.technicalInspectionDocumentUrl = upload.url;
                    } else if (fileType === 'OPERATING_CARD') {
                        this.vehicle.operatingCardDocumentUrl = upload.url;
                    }
                    this.vehicleCreationInProgress = false;
                    this.messageService.add({ severity: 'success', summary: 'Success', detail: 'File uploaded successfully.' });
                }
            },
            error: () => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'File upload failed.' });
                this.vehicleCreationInProgress = false;
                this.pdfUploadProgress = 0;
            }
        });
    }

    saveVehicle(): void {
        if (this.isRental && !this.vehicle.vehicleId) {
            this.messageService.add({
                severity: 'info',
                summary: 'Create rental assets from an agreement',
                detail: 'Rental vehicles must be added from Fleet > Rental > Agreements so the agreement, rate card, and effective dates are captured together.'
            });
            if (this.embedded) {
                this.cancelled.emit();
            } else {
                this.router.navigate(['/fleet/rentals/agreements']);
            }
            return;
        }

        if (this.createEditVehicleForm.invalid) {
            this.createEditVehicleForm.markAllAsTouched();
            this.messageService.add({
                severity: 'warn',
                summary: 'Validation Error',
                detail: 'Please complete the required vehicle fields before saving.'
            });
            return;
        }

        this.vehicleCreationInProgress = true;
        this.syncVehicleFromForm();
        this.vehicle.vehicleImages = this.vehicle.vehicleImages || [];

        if (this.isFreelancer) {
            const isUpdate = !!this.vehicle.vehicleId;
            this.vehicleService.addFreelancerVehicle(this.vehicle, this.sessionStorageService.getCompany()!.id).subscribe({
                next: response => {
                    if (response.success) {
                        const action = isUpdate ? 'updated' : 'created';
                        this.messageService.add({ severity: 'success', summary: `Vehicle ${this.vehicle.plateNumber} is ${action}`, detail: `Vehicle ${this.vehicle.plateNumber} has been successfully ${action}` });
                        this.handleSuccessfulSave(response.data || this.vehicle);
                    } else {
                        this.messageService.add({ severity: 'error', summary: 'Error', detail: `Failed to add vehicle. ${response.message || ''}` });
                    }
                    this.vehicleCreationInProgress = false;
                },
                error: error => {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: `Failed to add vehicle. ${error.message}` });
                    this.vehicleCreationInProgress = false;
                }
            });
            return;
        }

        const transportationAgencyId = this.resolveTransportationAgencyId();
        if (!transportationAgencyId) {
            this.showMissingTransportationAgencyError();
            return;
        }
        this.vehicle.transportationAgencyId = transportationAgencyId;

        const request = this.vehicle.vehicleId
            ? this.vehicleService.updateVehicle(this.vehicle)
            : this.vehicleService.addVehicle(this.vehicle);

        request.subscribe({
            next: response => {
                if (response.success) {
                    const action = this.vehicle.vehicleId ? 'updated' : 'created';
                    const summary = `Vehicle ${response.data.plateNumber} is ${action}`;
                    const detail = `Vehicle ${response.data.plateNumber} has been successfully ${action}`;
                    this.messageService.add({ severity: 'success', summary, detail });
                    this.handleSuccessfulSave(response.data);
                } else {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: `Failed to save vehicle. ${response.message || ''}` });
                }
                this.vehicleCreationInProgress = false;
            },
            error: error => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: `Failed to save vehicle. ${error.message}` });
                this.vehicleCreationInProgress = false;
            }
        });
    }

    cancelEmbedded(): void {
        this.cancelled.emit();
    }

    clearForm(): void {
        this.createEditVehicleForm.reset();
        this.vehicle = new Vehicle();
        this.vehicle.isInsured = false;
        this.selectedRegistrationDate = undefined;
        this.selectedRegistrationIssueDate = undefined;
        this.selectedInsuranceIssueDate = undefined;
        this.selectedInsuranceExpiryDate = undefined;
        this.selectedTechnicalInspectionIssueDate = undefined;
        this.selectedTechnicalInspectionExpiryDate = undefined;
        this.selectedOperatingCardIssueDate = undefined;
        this.selectedOperatingCardExpiryDate = undefined;
        this.selectedRegistrationCountry = undefined;
        this.selectedRegistrationCity = undefined;
        this.selectedOperatingCardCountry = undefined;
        this.selectedInsuranceCompany = null;
        this.registrationCities = [];
        this.insuranceCompanies = [];
        this.selectedTransporterClient = undefined;
    }

    isFieldInvalid(controlName: string): boolean {
        const control = this.createEditVehicleForm?.get(controlName);
        return !!control && control.invalid && (control.dirty || control.touched);
    }

    getFieldError(controlName: string): string {
        const control = this.createEditVehicleForm?.get(controlName);
        if (control?.hasError('required')) {
            return 'This field is required.';
        }
        if (control?.hasError('pattern')) {
            return 'Use letters and numbers only; spaces are removed automatically.';
        }
        return '';
    }

    private syncVehicleFromForm(): void {
        const plateNumber = this.createEditVehicleForm.get('plateNumberInput')?.value;
        if (typeof plateNumber === 'string') {
            this.vehicle.plateNumber = plateNumber.replace(/\s+/g, '').toUpperCase();
        }
        this.vehicle.type = this.selectedVehicleType!;
        this.vehicle.config = this.selectedVehicleConfig!;
        this.vehicle.capacity = this.selectedVehicleCapacity!;
        this.vehicle.make = this.selectedVehicleMake!;
        this.vehicle.model = this.selectedVehicleModel!;
        this.vehicle.year = this.selectedVehicleYear!;
        this.vehicle.payloadTypes = this.selectedPayloadTypes;
        this.vehicle.tripTypes = this.selectedTripTypes;
        this.vehicle.registrationCountry = this.selectedRegistrationCountry;
        this.vehicle.registrationCity = this.selectedRegistrationCity;
        this.vehicle.operatingCardCountry = this.selectedOperatingCardCountry;
        this.vehicle.insuranceCompany = this.selectedInsuranceCompany;
        this.vehicle.registrationExpiry = this.selectedRegistrationDate?.getTime() ?? new Date().getTime();
        this.vehicle.registrationIssueDate = this.selectedRegistrationIssueDate?.getTime();
        this.vehicle.insuranceIssueDate = this.selectedInsuranceIssueDate?.getTime();
        this.vehicle.insuranceExpiry = this.selectedInsuranceExpiryDate as any;
        this.vehicle.technicalInspectionIssueDate = this.selectedTechnicalInspectionIssueDate?.getTime();
        this.vehicle.technicalInspectionExpiryDate = this.selectedTechnicalInspectionExpiryDate?.getTime();
        this.vehicle.operatingCardIssueDate = this.selectedOperatingCardIssueDate?.getTime();
        this.vehicle.operatingCardExpiryDate = this.selectedOperatingCardExpiryDate?.getTime();
        this.vehicle.transporterCrmClientId = this.selectedTransporterClient?.id;
    }

    private handleSuccessfulSave(vehicle: Vehicle): void {
        if (this.embedded) {
            this.saved.emit(vehicle);
            return;
        }
        setTimeout(() => this.router.navigate(['/fleet/vehicles']), 2000);
    }

    private refreshCompanyFleetProfile(): void {
        const company = this.sessionStorageService.getCompany() ?? this.sessionStorageService.getCustomer()?.company;
        if (!company?.id || company.transportationAgencyId) {
            return;
        }

        this.companyService.getCompanyById(company.id).subscribe({
            next: response => {
                if (response.success && response.data) {
                    this.sessionStorageService.saveCompany(response.data);
                }
            }
        });
    }

    private initializeResourceContext(): void {
        if (this.embedded) {
            this.isFreelancer = this.embeddedSource === 'freelance';
            this.isRental = this.embeddedSource === 'rental';
        }

        const source = this.route.snapshot.queryParamMap.get('source');
        if (source === 'freelance') {
            this.isFreelancer = true;
            this.isRental = false;
        } else if (source === 'rental') {
            this.isRental = true;
            this.isFreelancer = false;
        } else if (source === 'owned') {
            this.isFreelancer = false;
            this.isRental = false;
        }

        if (this.route.snapshot.queryParamMap.get('isFreelancer')) {
            this.isFreelancer = this.route.snapshot.queryParamMap.get('isFreelancer') === 'true';
        }
        if (this.route.snapshot.queryParamMap.get('isRental')) {
            this.isRental = this.route.snapshot.queryParamMap.get('isRental') === 'true';
        }

    }

    private syncSelectedTransporterClient(): void {
        this.selectedTransporterClient = this.transporterClients.find(client => client.id === this.vehicle.transporterCrmClientId);
    }

    private resolveTransportationAgencyId(): number | undefined {
        return this.vehicle.transportationAgencyId
            ?? this.sessionStorageService.getCompany()?.transportationAgencyId
            ?? this.sessionStorageService.getCustomer()?.company?.transportationAgencyId
            ?? this.sessionStorageService.getTransportationAgency()?.rowId
            ?? undefined;
    }

    private showMissingTransportationAgencyError(): void {
        this.vehicleCreationInProgress = false;
        this.messageService.add({
            severity: 'error',
            summary: 'Transportation agency missing',
            detail: 'This company is not linked to a transportation agency. Refresh the session or complete company fleet setup before saving a vehicle.'
        });
    }

    private toDate(value?: number | Date): Date | undefined {
        if (!value) {
            return undefined;
        }
        return value instanceof Date ? value : new Date(value);
    }
}
